/**
 * Money helpers. Golden rule (CLAUDE.md §3.1): money is an integer number of Iraqi dinars.
 *
 * - The database stores IQD as BIGINT. In TypeScript an amount is a `number` that must be a
 *   safe integer (|n| ≤ 9,007,199,254,740,991 IQD — far above any real amount). Every helper
 *   validates this and throws instead of producing a fraction.
 * - Rates are integer basis points: 10,000 bp = 100%, 500 bp = 5%, 1 bp = 0.01%.
 * - Any operation that can produce a fraction takes an explicit rounding mode. There is no
 *   default, so every rounding rule in the system is visible at the call site and testable.
 * - Intermediate products are computed with BigInt, so `amount × rate` can never overflow.
 */

declare const iqdBrand: unique symbol;
declare const bpBrand: unique symbol;

/** An integer amount of Iraqi dinars. Create with {@link iqd}. */
export type IQD = number & { readonly [iqdBrand]: true };

/** An integer rate in basis points (10,000 = 100%). Create with {@link bp}. */
export type BasisPoints = number & { readonly [bpBrand]: true };

export const BASIS_POINTS_100_PERCENT = 10_000;

/**
 * - `HALF_UP`: nearest; exact halves go away from zero (2.5 → 3, −2.5 → −3).
 * - `HALF_EVEN`: nearest; exact halves go to the even neighbour (2.5 → 2, 3.5 → 4).
 * - `DOWN`: toward zero (2.9 → 2, −2.9 → −2).
 * - `UP`: away from zero (2.1 → 3, −2.1 → −3).
 * - `FLOOR`: toward −∞ (−2.1 → −3).
 * - `CEILING`: toward +∞ (−2.9 → −2).
 */
export type RoundingMode = 'HALF_UP' | 'HALF_EVEN' | 'DOWN' | 'UP' | 'FLOOR' | 'CEILING';

export class MoneyError extends Error {
  override readonly name = 'MoneyError';
}

/** Validates and brands an integer amount of dinars. Throws on fractions, NaN, ±Infinity or unsafe integers. */
export function iqd(value: number): IQD {
  if (!Number.isSafeInteger(value)) {
    throw new MoneyError(`IQD amount must be a safe integer, got ${String(value)}`);
  }
  // Normalise −0 to 0 so it never leaks into JSON or equality checks.
  return (value === 0 ? 0 : value) as IQD;
}

/** Validates and brands a non-negative integer rate in basis points. */
export function bp(value: number): BasisPoints {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new MoneyError(
      `Rate must be a non-negative integer number of basis points, got ${String(value)}`,
    );
  }
  return value as BasisPoints;
}

function fromBigInt(value: bigint): IQD {
  if (value > BigInt(Number.MAX_SAFE_INTEGER) || value < BigInt(Number.MIN_SAFE_INTEGER)) {
    throw new MoneyError(`IQD amount overflow: ${value.toString()}`);
  }
  return iqd(Number(value));
}

export function addIQD(a: IQD, b: IQD): IQD {
  return fromBigInt(BigInt(iqd(a)) + BigInt(iqd(b)));
}

export function subtractIQD(a: IQD, b: IQD): IQD {
  return fromBigInt(BigInt(iqd(a)) - BigInt(iqd(b)));
}

export function sumIQD(amounts: readonly IQD[]): IQD {
  let total = 0n;
  for (const amount of amounts) total += BigInt(iqd(amount));
  return fromBigInt(total);
}

/** Integer multiplication, e.g. unit price × quantity. `factor` must be an integer. */
export function multiplyIQD(amount: IQD, factor: number): IQD {
  if (!Number.isSafeInteger(factor)) {
    throw new MoneyError(`Multiplication factor must be an integer, got ${String(factor)}`);
  }
  return fromBigInt(BigInt(iqd(amount)) * BigInt(factor));
}

/** Exact integer division `numerator / denominator` rounded with `mode`. The core of every rounding rule. */
export function divideRounded(numerator: bigint, denominator: bigint, mode: RoundingMode): bigint {
  if (denominator === 0n) throw new MoneyError('Division by zero');
  if (denominator < 0n) {
    numerator = -numerator;
    denominator = -denominator;
  }
  const quotient = numerator / denominator; // BigInt division truncates toward zero
  const remainder = numerator % denominator; // has the sign of the numerator
  if (remainder === 0n) return quotient;

  const sign = numerator < 0n ? -1n : 1n;
  const awayFromZero = quotient + sign;
  const twiceRemainder = (remainder < 0n ? -remainder : remainder) * 2n;

  switch (mode) {
    case 'DOWN':
      return quotient;
    case 'UP':
      return awayFromZero;
    case 'FLOOR':
      return sign < 0n ? awayFromZero : quotient;
    case 'CEILING':
      return sign > 0n ? awayFromZero : quotient;
    case 'HALF_UP':
      return twiceRemainder >= denominator ? awayFromZero : quotient;
    case 'HALF_EVEN':
      if (twiceRemainder > denominator) return awayFromZero;
      if (twiceRemainder < denominator) return quotient;
      return quotient % 2n === 0n ? quotient : awayFromZero;
  }
}

/** `amount × rate`, e.g. a 5% royalty or a 15% platform commission, rounded with `mode`. */
export function percentOf(amount: IQD, rate: BasisPoints, mode: RoundingMode): IQD {
  const product = BigInt(iqd(amount)) * BigInt(bp(rate));
  return fromBigInt(divideRounded(product, BigInt(BASIS_POINTS_100_PERCENT), mode));
}

/** Rounds to a multiple of `unit` dinars, e.g. 250 (smallest note in use) or 1,000. */
export function roundToUnit(amount: IQD, unit: number, mode: RoundingMode): IQD {
  if (!Number.isSafeInteger(unit) || unit <= 0) {
    throw new MoneyError(`Rounding unit must be a positive integer, got ${String(unit)}`);
  }
  const units = divideRounded(BigInt(iqd(amount)), BigInt(unit), mode);
  return fromBigInt(units * BigInt(unit));
}

/**
 * How to treat the dinars left over when a total cannot be split exactly.
 * - `LARGEST_REMAINDER`: hand out the leftover dinars one by one to the shares with the largest
 *   fractional part; ties go to the lower index (the order of `weights`). Shares sum exactly to the total.
 * - `CARRY_REMAINDER`: every share is rounded down and the leftover is returned as `remainder`
 *   (for example, kept as retained earnings and carried to the next distribution).
 */
export type RemainderPolicy = 'LARGEST_REMAINDER' | 'CARRY_REMAINDER';

export interface Allocation {
  readonly shares: readonly IQD[];
  /** Dinars not allocated. Always 0 for `LARGEST_REMAINDER`. */
  readonly remainder: IQD;
}

/**
 * Splits a non-negative `total` in proportion to integer `weights` (typically basis points),
 * e.g. partner profit distribution (4 × 2,500 bp).
 */
export function allocate(
  total: IQD,
  weights: readonly number[],
  policy: RemainderPolicy,
): Allocation {
  const amount = BigInt(iqd(total));
  if (amount < 0n) throw new MoneyError('Cannot allocate a negative total');
  if (weights.length === 0) throw new MoneyError('At least one weight is required');

  let weightSum = 0n;
  for (const weight of weights) {
    if (!Number.isSafeInteger(weight) || weight < 0) {
      throw new MoneyError(`Weights must be non-negative integers, got ${String(weight)}`);
    }
    weightSum += BigInt(weight);
  }
  if (weightSum === 0n) throw new MoneyError('Weights must not all be zero');

  const floors = weights.map((w) => (amount * BigInt(w)) / weightSum);
  const fractions = weights.map((w) => (amount * BigInt(w)) % weightSum);
  let leftover = amount - floors.reduce((a, b) => a + b, 0n);

  if (policy === 'LARGEST_REMAINDER') {
    const order = weights
      .map((_, index) => index)
      .sort((a, b) => {
        const fa = fractions[a] ?? 0n;
        const fb = fractions[b] ?? 0n;
        if (fa === fb) return a - b;
        return fa > fb ? -1 : 1;
      });
    for (const index of order) {
      if (leftover === 0n) break;
      floors[index] = (floors[index] ?? 0n) + 1n;
      leftover -= 1n;
    }
  }

  return { shares: floors.map(fromBigInt), remainder: fromBigInt(leftover) };
}

/** Throws unless the rates add up to exactly 100% (10,000 bp), e.g. partner shares. */
export function assertTotals100Percent(rates: readonly BasisPoints[]): void {
  let total = 0;
  for (const rate of rates) total += bp(rate);
  if (total !== BASIS_POINTS_100_PERCENT) {
    throw new MoneyError(`Shares must total 100% (10,000 bp), got ${String(total)} bp`);
  }
}
